import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCart } from "@/context/CartContext";
import { TAX_RATE } from "@/lib/constants";
import { devError } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "test";

const Checkout = () => {
  const { items, subtotal, rawItems, clear } = useCart();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [paypalConfigured, setPaypalConfigured] = useState(false);
  const [processing, setProcessing] = useState(false);

  const tax = useMemo(() => +(subtotal * TAX_RATE).toFixed(2), [subtotal]);
  const total = useMemo(() => +(subtotal + tax).toFixed(2), [subtotal, tax]);

  useEffect(() => {
    axios
      .get(`${API}/paypal/config`)
      .then((r) => setPaypalConfigured(r.data.configured))
      .catch(() => setPaypalConfigured(false));
  }, []);

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-black tracking-tighter">Your cart is empty</h1>
        <p className="mt-2 text-slate-500">Add some protection to checkout.</p>
        <Button asChild className="mt-6 rounded-sm bg-emerald-500 text-slate-900 hover:bg-emerald-400">
          <Link to="/products">Browse Products</Link>
        </Button>
      </div>
    );
  }

  const handleMockCheckout = async () => {
    setProcessing(true);
    try {
      const orderRes = await axios.post(`${API}/checkout/create-order`, {
        items: rawItems,
        email,
      });
      const { order_id } = orderRes.data;
      const capRes = await axios.post(`${API}/checkout/capture-order`, {
        order_id,
      });
      const confirmation = capRes.data.confirmation;
      clear();
      navigate(`/success?ref=${confirmation}&total=${total}&email=${encodeURIComponent(email)}`);
    } catch (e) {
      devError("Mock checkout failed:", e);
      toast.error("Checkout failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/products"
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" /> Continue shopping
          </Link>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 lg:grid-cols-12 lg:px-8">
        {/* LEFT: Form */}
        <div className="lg:col-span-7">
          <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            Checkout
          </h1>
          <p className="mt-2 text-slate-600">
            Guest checkout — your license keys arrive instantly by email.
          </p>

          <div className="mt-8 border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Contact details
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider">
                  Full Name
                </Label>
                <Input
                  id="name"
                  data-testid="checkout-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="mt-1 rounded-sm border-slate-300 focus-visible:ring-emerald-500"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="checkout-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 rounded-sm border-slate-300 focus-visible:ring-emerald-500"
                />
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              We&apos;ll only use your email to deliver activation keys and order confirmation.
            </p>
          </div>

          <div className="mt-6 border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Payment method
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Lock className="h-3 w-3" /> Encrypted
              </div>
            </div>

            <div className="mt-4 border-2 border-slate-900 bg-emerald-50 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-16 items-center justify-center bg-white font-display text-base font-black italic text-blue-700 ring-1 ring-slate-200">
                  Pay<span className="text-blue-900">Pal</span>
                </div>
                <div>
                  <div className="font-display text-base font-bold text-slate-900">
                    Pay securely with PayPal
                  </div>
                  <div className="text-xs text-slate-600">
                    You&apos;ll confirm your payment in a PayPal popup.
                  </div>
                </div>
              </div>

              <div className="mt-5">
                {paypalConfigured ? (
                  <PayPalScriptProvider
                    options={{ "client-id": PAYPAL_CLIENT_ID, currency: "USD" }}
                  >
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                      disabled={!email}
                      createOrder={async () => {
                        const res = await axios.post(`${API}/checkout/create-order`, {
                          items: rawItems,
                          email,
                        });
                        return res.data.order_id;
                      }}
                      onApprove={async (data) => {
                        const cap = await axios.post(`${API}/checkout/capture-order`, {
                          order_id: data.orderID,
                        });
                        clear();
                        navigate(
                          `/success?ref=${cap.data.confirmation}&total=${total}&email=${encodeURIComponent(email)}`,
                        );
                      }}
                      onError={(err) => {
                        devError("PayPal Buttons error:", err);
                        toast.error("PayPal error. Please retry.");
                      }}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <div>
                    <div className="mb-3 border border-emerald-400 bg-emerald-100 px-3 py-2 text-xs text-emerald-900">
                      <span className="font-bold">Demo mode:</span> PayPal keys not configured — checkout
                      will simulate a successful payment.
                    </div>
                    <Button
                      onClick={handleMockCheckout}
                      disabled={!email || processing}
                      data-testid="mock-paypal-checkout-btn"
                      className="w-full rounded-sm bg-emerald-500 py-6 text-base font-bold text-white hover:bg-emerald-600"
                    >
                      {processing ? "Processing…" : `Pay $${total.toFixed(2)} with PayPal`}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              By placing this order you agree to our terms & 30-day refund policy.
            </div>
          </div>
        </div>

        {/* RIGHT: Order summary */}
        <aside className="lg:col-span-5">
          <div className="sticky top-24 border-2 border-slate-900 bg-white">
            <div className="border-b border-slate-200 px-6 py-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Order summary
              </div>
            </div>
            <ul className="divide-y divide-slate-200 px-6">
              {items.map((it) => (
                <li
                  key={`${it.product_id}-${it.plan_id}`}
                  className="flex items-start justify-between py-4"
                  data-testid={`summary-item-${it.product_id}`}
                >
                  <div className="pr-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                      {it.product.category}
                    </div>
                    <div className="font-display text-base font-bold tracking-tight text-slate-900">
                      {it.product.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {it.plan.label} · Qty {it.quantity}
                    </div>
                  </div>
                  <div className="font-display text-base font-black text-slate-900">
                    ${it.line_total.toFixed(2)}
                  </div>
                </li>
              ))}
            </ul>
            <div className="space-y-2 border-t border-slate-200 px-6 py-4 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Estimated tax (8%)</span>
                <span data-testid="summary-tax">${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-3">
                <span className="font-display text-base font-bold text-slate-900">Total</span>
                <span
                  data-testid="summary-total"
                  className="font-display text-2xl font-black tracking-tighter text-slate-900"
                >
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
